import type { Question } from '@mentimeter/legacy-series-types/question';
import { handleNoInteractiveContentError } from '../../applications/common/handle-no-interactive-content-error';
import type {
  InteractiveContent as VotingInteractiveContent,
  Slide as VotingSlide,
} from '../../compatibility-overrides/voting-types';
import type {
  CompatibilityInteractiveContent,
  CompatibilitySlide,
} from '../../compatibility-types';
import { VOTE_ON_RESPONSE_ALLOW } from '../../constants';
import type { CommonQuestionProperty } from './get-common-question-property';

const getMultipleVotesOnAnswers = (
  interactiveContent:
    | CompatibilityInteractiveContent
    | VotingInteractiveContent,
) => {
  const maxEntriesPerResponse =
    interactiveContent.voteSettings?.maxEntriesPerResponse;
  return (
    typeof maxEntriesPerResponse === 'undefined' ||
    maxEntriesPerResponse === null
  );
};

const getMaxVotes = (
  interactiveContent:
    | CompatibilityInteractiveContent
    | VotingInteractiveContent,
) => {
  if (!interactiveContent.voteSettings?.maxEntries) {
    return -1; // legacy default value
  }

  return interactiveContent.voteSettings.maxEntries;
};

export const getQuestionOpenEndedProperty = (
  target: CompatibilitySlide<'open-ended'> | VotingSlide<'open-ended'>,
  prop: keyof Question,
) => {
  const interactiveContents = target.interactiveContents[0];
  const staticContents = target.staticContent;

  if (prop === 'question_styled') {
    return staticContents.styledTitle ?? {};
  }

  if (interactiveContents) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'multiple_votes_on_answers': {
        return getMultipleVotesOnAnswers(interactiveContents);
      }
      case 'max_votes': {
        return getMaxVotes(interactiveContents);
      }
      case 'voting_on_answers': {
        return (
          interactiveContents.voteSettings?.voteOnResponsePolicy ===
          VOTE_ON_RESPONSE_ALLOW
        );
      }
      default:
        return undefined;
    }
  }

  return handleNoInteractiveContentError(target, prop);
};
